import React, { useEffect, useState } from "react";
import SelectVariant from "@components/select-variant";
import AddToCartButton from "@components/ui/add-to-cart-button";
import * as Styled from "@components/product/Product.styled";
import { Shopify } from "@models/shopify.model";
import { SanitySyncProductDocument } from "@models/product.model";
import { BundleOption } from "./ProductConfiguration.model";
import { useShopifyCart } from "@providers/cart";
import { useUserProfile } from "@providers/profile";
import { SelectedOption } from "@utils/deriveVariantFromOptions";
import { firstAvailableVariant } from "@utils/firstAvailableVariant";
import useFastBundleAPI from "@hooks/useFastBundleAPI";
import {
  deriveFastBundleVariantFromOptions,
  setFastBundleOptions,
} from "@utils/fastBundleVariants";
import { gidFastBundleProductVariantId } from "@models/fastbundle-api.model";

export interface ProductConfigurationProps {
  productDocument: SanitySyncProductDocument;
  storefrontProduct: Shopify.Product;
  addToCartText?: string | React.ReactElement;
  children?: React.ReactNode;
  variantId: Shopify.ProductVariantId | "";
  onVariantDerived: (variantId: Shopify.ProductVariantId | "") => void;
}

const ProductConfiguration = ({
  productDocument,
  storefrontProduct,
  addToCartText,
  children,
  variantId,
  onVariantDerived,
}: ProductConfigurationProps) => {
  const { addItem, setCartVisible, checkout } = useShopifyCart();
  const {
    profileState: { authState },
  } = useUserProfile();
  const [selectedOptions, setSelectedOptions] = useState<{ [x: string]: SelectedOption }>({});
  const storefrontProductNumber = storefrontProduct?.id.split("/").pop() as string;
  const { fastBundleProduct } = useFastBundleAPI(storefrontProductNumber);
  const linkedProductVariantId =
    firstAvailableVariant(storefrontProduct?.linkedProduct?.reference)?.id || "";

  useEffect(() => {
    if (!storefrontProduct) return;
    /**
     * Check if we have enough options populated to derive a variant.
     */
    const selectionsRequiredCount =
      productDocument.bundle_settings?.included_products?.reduce((cumulative, included) => {
        return cumulative + included.product.options.length * included.quantity;
      }, 0) || 0;
    const selectedOptionsLength = Object.keys(selectedOptions).length;
    if (
      selectedOptionsLength < selectionsRequiredCount ||
      (selectedOptionsLength == 0 && selectedOptionsLength == selectionsRequiredCount)
    )
      return;

    const selectedValues = Object.values(selectedOptions);
    const derivedFastBundleVariant = deriveFastBundleVariantFromOptions(
      selectedValues,
      fastBundleProduct
    );
    const derivedFastBundleVariantId = gidFastBundleProductVariantId(derivedFastBundleVariant);

    /**
     * Resets variant state when provided a falsy value.
     */
    onVariantDerived(derivedFastBundleVariantId || "");
  }, [selectedOptions, fastBundleProduct, storefrontProduct]);

  const handleVariantChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    { key, position }: { key: string; position: number }
  ) => {
    setSelectedOptions({
      ...selectedOptions,
      [key]: {
        value: e.target.value,
        position: position,
      },
    });
  };

  const handleClickAddToCart = (variantId: string, linkedVariantId: string) => {
    /**
     * If the linked product is already in the cart,
     * don't add another one.
     */
    const linkedProductInCart = checkout.lineItems.find(
      (line) => line.variant.id === linkedVariantId
    );

    if (linkedProductInCart || !linkedVariantId) {
      addItem(authState, { variantId, quantity: 1 });
    } else {
      addItem(
        authState,
        {
          variantId,
          quantity: 1,
        },
        {
          variantId: linkedVariantId,
          quantity: 1,
        }
      );
    }

    setCartVisible(true);
  };

  return (
    <>
      {productDocument.bundle_settings?.included_products?.map(
        ({ product, quantity }, productIndex) => (
          <Styled.SubItemSelectBlock key={productIndex}>
            <Styled.SubItemSelectHeader>
              <h4>{`Choose ${product.title}`}</h4>
              <p>{product.cart_description || null}</p>
            </Styled.SubItemSelectHeader>
            <Styled.SelectRow>
              {Array.from(Array(quantity).keys()).map((quantityIndex) => {
                const bundleOption: BundleOption = {
                  key: `included-product-${productIndex}-option-${quantityIndex}`,
                  product,
                  productIndex,
                  quantityIndex,
                  values:
                    storefrontProduct && fastBundleProduct
                      ? setFastBundleOptions(fastBundleProduct.items)
                      : [],
                };

                return (
                  <div key={quantityIndex} className="variant-select">
                    <SelectVariant
                      label={product.title}
                      id={`${bundleOption.product.title}-${quantityIndex}`}
                      bundleOption={bundleOption}
                      onChange={(e) =>
                        handleVariantChange(e, {
                          key: bundleOption.key,
                          position: productIndex + quantityIndex,
                        })
                      }
                      value={selectedOptions[bundleOption.key]?.value || ""}
                    />
                  </div>
                );
              })}
            </Styled.SelectRow>
          </Styled.SubItemSelectBlock>
        )
      )}
      {children}
      <AddToCartButton
        disabled={!variantId}
        productId={productDocument._id}
        onClick={() => handleClickAddToCart(variantId, linkedProductVariantId)}>
        {addToCartText}
      </AddToCartButton>
    </>
  );
};

export default ProductConfiguration;
